import React from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
} from "@mui/material";
import StartOutlinedIcon from "@mui/icons-material/StartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons";
import { H7TitleLarge } from "../../../components/StyledComponents/Typography/Typography.tsx";
import SiteAndGroupTree from "../../../minerstatusV2/Components/LeftPanel/SiteAndGroupTree/SiteAndGroupTree.js";
import { set } from "date-fns";
import { useGetSiteLists } from "../../../api/queries/useGetSiteLists.ts";
import AuradineErrorState from "../../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import AuradineDataNotFoundState from "../../../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

export const LeftSidePanel = React.memo((props) => {
  const { colors } = useTheme();
  const {
    leftSidePanelOpen,
    setLeftSidePanelOpen,
    height,
    authToken,
  } = props;
  const [siteAndGroups, setSiteAndGroups] =
    React.useState();

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    isRefetching: isSiteGroupDataRefetching,
    refetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: leftSidePanelOpen,
  });

  React.useEffect(() => {
    const siteGroup =
      JSON.parse(sessionStorage.getItem("siteGroup")) || {};
    if (siteGroup.siteName || siteGroup.dgname) {
      setLeftSidePanelOpen(true);
    }
  }, []);

  React.useEffect(() => {
    if (siteLists) {
      if (
        siteLists?.status === "ok" &&
        siteLists?.siteList?.length > 0
      ) {
        const siteList = siteLists?.siteList;
        setSiteAndGroups(siteList);
      }
    }
  }, [siteLists, isSiteGroupDataFetching]);

  const onSitesAndGroupSearch = (e) => {
    if (e.target.value === "") {
      setSiteAndGroups(siteLists?.siteList);
      return;
    } else {
      const sitesData = [...siteAndGroups];
      const foundSite = sitesData.filter((site) => {
        return (
          site?.siteName
            .toLowerCase()
            .includes(e.target.value.toLowerCase()) ||
          (site.groups &&
            site.groups.some((group) =>
              group.DGName.toLowerCase().includes(
                e.target.value.toLowerCase()
              )
            ))
        );
      });

      setSiteAndGroups(foundSite);
    }
  };
  const [searchValue, setSearchValue] = React.useState("");
  return (
    <Box
      className={"left-side-panel"}
      display={"flex"}
      flexDirection={"column"}
      gap={4}
      height={height}
      paddingLeft={"0.75rem"}
      paddingRight={"0.75rem"}
      minWidth={leftSidePanelOpen ? "17vw" : "7vw"}
      width={leftSidePanelOpen ? "17vw" : "7vw"}
      flexGrow={leftSidePanelOpen ? 0.1 : 0}
      flexShrink={0}
      borderRight={"1px solid " + colors?.border?.default}
      sx={{
        transition: "all 0.3s ease-in-out",
      }}
    >
      <Box
        className={"control-button"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={
          leftSidePanelOpen ? "space-between" : "center"
        }
      >
        {leftSidePanelOpen && (
          <H7TitleLarge>Sites & Groups</H7TitleLarge>
        )}
        <Tooltip title="Sites & Groups">
          <span>
            <StyledIconButton
              onClick={() => {
                setLeftSidePanelOpen((prev) => !prev);
              }}
            >
              {leftSidePanelOpen ? (
                <span
                  style={{
                    height: "24px",
                    width: "24px",
                    color: colors.text.muted,
                  }}
                  className="material-symbols-outlined"
                >
                  right_panel_open
                </span>
              ) : (
                <span
                  style={{
                    height: "24px",
                    width: "24px",
                    color: colors.text.muted,
                  }}
                  className="material-symbols-outlined"
                >
                  right_panel_close
                </span>
              )}
            </StyledIconButton>
          </span>
        </Tooltip>
      </Box>
      {leftSidePanelOpen && (
        <>
          <Collapse
            in={leftSidePanelOpen}
            collapsedSize={0}
          >
            {siteLists &&
              siteLists.status === "ok" &&
              Array.isArray(siteLists.siteList) &&
              siteLists.siteList.length > 0 && (
                <Paper
                  component="form"
                  elevation={0}
                  sx={{
                    p: "2px 4px",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    backgroundColor: colors.background.secondary,
                    borderRadius: "1.75rem",
                    paddingLeft: "1rem",
                  }}
                >
                  <Tooltip
                    title={searchValue}
                    disableFocusListener
                    disableTouchListener
                  >
                    <InputBase
                      sx={{ flex: 1, "& .MuiInputBase-input": {
                        color: colors.text.primary,             // normal text
                        "::placeholder": {
                          color: colors.text.hint,
                        },
                      }}}
                      disabled={
                        siteLists &&
                        siteLists?.status !== "ok"
                      }
                      placeholder="Search for a site or group"
                      onChange={(event) => {
                        setSearchValue(event.target.value);
                        onSitesAndGroupSearch(event);
                      }}
                      // multiline
                      // maxRows={4}
                      inputProps={{
                        "aria-label": "search google maps",
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                        }
                      }}
                    />
                  </Tooltip>
                  <IconButton
                    type="button"
                    sx={{ p: "10px", color: colors.text.hint }}
                    aria-label="search"
                  >
                    <SearchIcon />
                  </IconButton>
                </Paper>
              )}
            {(isSiteGroupDataLoading ||
              isSiteGroupDataFetching ||
              isSiteGroupDataRefetching) && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                flexGrow={1}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <CircularProgress
                  size={"1.5rem"}
                  sx={{
                    color: colors.primary.main,
                  }}
                />
              </Box>
            )}

            {isSiteGroupDataFetched &&
              siteLists?.status === "ok" &&
              siteLists &&
              siteLists?.siteList?.length > 0 && (
                <>
                  <SiteAndGroupTree
                    siteAndGroups={siteAndGroups}
                    onSitesAndGroupSearch={
                      onSitesAndGroupSearch
                    }
                    isSiteGroupDataFetched={
                      isSiteGroupDataFetched
                    }
                    sidePanelMode={true}
                  />
                </>
              )}
            {isSiteGroupDataFetched &&
              siteLists?.status === "ok" &&
              siteLists?.siteList?.length === 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                >
                  <AuradineDataNotFoundState
                    message={`You may not have permissions to any sites. Please contact superadmin`}
                    fontSize="1rem"
                  />
                </Box>
              )}
            {isSiteGroupDataFetched &&
              siteLists?.status !== "ok" && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexGrow={1}
                >
                  <AuradineErrorState
                    message={
                      siteLists && siteLists.status !== "ok"
                        ? `${siteLists.msg}. Please contact your superadmin.`
                        : `You may not have permissions to any sites. Please contact superadmin`
                    }
                    fontSize="1rem"
                  />
                </Box>
              )}
          </Collapse>
        </>
      )}
    </Box>
  );
});
